import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Plus, Wrench } from "lucide-react";
import { toast } from "sonner";
import type { MaintenanceWindow } from "@shared/types";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import * as api from "@/lib/api";
import { queryKeys, useMaintenanceWindows, useMonitors } from "@/lib/queries";
import { formatDateTime } from "@/lib/utils";

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}

function toLocalInput(ms: number) {
  const date = new Date(ms);
  return new Date(ms - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function WindowDialog({
  window,
  onOpenChange,
}: {
  window: MaintenanceWindow | null;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data: monitors, isLoading: monitorsLoading } = useMonitors();
  const initialStart = window?.startAt ?? Date.now();
  const [title, setTitle] = useState(window?.title ?? "");
  const [start, setStart] = useState(toLocalInput(initialStart));
  const [end, setEnd] = useState(toLocalInput(window?.endAt ?? initialStart + 60 * 60_000));
  const [monitorIds, setMonitorIds] = useState<number[]>(window?.monitorIds ?? []);
  const [busy, setBusy] = useState(false);
  const startAt = new Date(start).getTime();
  const endAt = new Date(end).getTime();
  const dateError = Number.isFinite(startAt) && Number.isFinite(endAt) && endAt > startAt ? "" : "End must be after start";
  const allSelected = Boolean(monitors?.length) && monitors?.every((monitor) => monitorIds.includes(monitor.id));

  function toggleMonitor(id: number, checked: boolean) {
    setMonitorIds((current) => checked ? [...current, id] : current.filter((monitorId) => monitorId !== id));
  }

  function toggleAll(checked: boolean) {
    setMonitorIds(checked ? (monitors?.map((monitor) => monitor.id) ?? []) : []);
  }

  async function save() {
    if (!title.trim() || dateError) return;
    setBusy(true);
    try {
      const input = { title: title.trim(), startAt, endAt, monitorIds };
      if (window) await api.updateMaintenance(window.id, input);
      else await api.createMaintenance(input);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.maintenance }),
        queryClient.invalidateQueries({ queryKey: queryKeys.monitors }),
      ]);
      toast.success(window ? "Maintenance window updated" : "Maintenance window scheduled");
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{window ? "Edit maintenance window" : "Schedule maintenance"}</DialogTitle><DialogDescription>Alerts are silenced for selected monitors during this window.</DialogDescription></DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-2"><Label htmlFor="maintenance-title">Title</Label><Input id="maintenance-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Database upgrade" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="maintenance-start">Start</Label><Input id="maintenance-start" type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} /></div>
            <div className="space-y-2"><Label htmlFor="maintenance-end">End</Label><Input id="maintenance-end" type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} /></div>
          </div>
          {dateError && <p className="text-sm text-destructive">{dateError}</p>}
          <div className="space-y-2">
            <Label>Monitors</Label>
            <div className="rounded-lg border">
              <label className="flex items-center gap-3 border-b px-3 py-2.5 text-sm font-medium">
                <Checkbox checked={allSelected} onCheckedChange={(checked) => toggleAll(checked === true)} />Select all
              </label>
              <ScrollArea className="h-64">
                <div className="space-y-1 p-2">
                  {monitorsLoading ? [0, 1, 2].map((item) => <Skeleton key={item} className="h-9" />) : monitors?.length ? monitors.map((monitor) => (
                    <label key={monitor.id} className="flex items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted/50">
                      <Checkbox checked={monitorIds.includes(monitor.id)} onCheckedChange={(checked) => toggleMonitor(monitor.id, checked === true)} />
                      <span className="truncate">{monitor.name}</span>
                    </label>
                  )) : <p className="p-4 text-center text-sm text-muted-foreground">No monitors available</p>}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={() => void save()} disabled={busy || !title.trim() || Boolean(dateError)}>{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WindowStatus({ window }: { window: MaintenanceWindow }) {
  const now = Date.now();
  if (now < window.startAt) return <Badge variant="secondary">Upcoming</Badge>;
  if (now > window.endAt) return <Badge variant="outline">Ended</Badge>;
  return <Badge className="border-status-maintenance/30 bg-status-maintenance/10 text-status-maintenance">Active</Badge>;
}

export default function MaintenancePage() {
  const { data: windows, isLoading } = useMaintenanceWindows();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ key: number; window: MaintenanceWindow | null } | null>(null);
  const [deleting, setDeleting] = useState<MaintenanceWindow | null>(null);
  const [busy, setBusy] = useState(false);

  function openDialog(window: MaintenanceWindow | null) {
    setDialog({ key: Date.now(), window });
  }

  async function deleteWindow() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.deleteMaintenance(deleting.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.maintenance }),
        queryClient.invalidateQueries({ queryKey: queryKeys.monitors }),
      ]);
      toast.success("Maintenance window deleted");
      setDeleting(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl mx-auto w-full">
      <PageHeader title="Maintenance" description="Silence alerts during planned work" actions={<Button onClick={() => openDialog(null)}><Plus />Schedule window</Button>} />
      {isLoading ? <div className="space-y-3">{[0, 1, 2].map((item) => <Skeleton key={item} className="h-24 rounded-xl" />)}</div> : !windows?.length ? (
        <EmptyState icon={Wrench} title="No maintenance scheduled" description="Plan a window to silence alerts while work is underway." action={<Button onClick={() => openDialog(null)}><Plus />Schedule window</Button>} />
      ) : (
        <div className="space-y-3">
          {windows.map((window) => (
            <Card key={window.id}>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><h2 className="font-semibold">{window.title}</h2><WindowStatus window={window} /></div>
                  <p className="mt-1 text-sm text-muted-foreground">{formatDateTime(window.startAt)} – {formatDateTime(window.endAt)}</p>
                  <Badge variant="outline" className="mt-3 tabular-nums">{window.monitorIds.length} {window.monitorIds.length === 1 ? "monitor" : "monitors"}</Badge>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" aria-label={`Actions for ${window.title}`}><MoreHorizontal /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end"><DropdownMenuItem onClick={() => openDialog(window)}>Edit</DropdownMenuItem><DropdownMenuItem variant="destructive" onClick={() => setDeleting(window)}>Delete</DropdownMenuItem></DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {dialog && <WindowDialog key={dialog.key} window={dialog.window} onOpenChange={(open) => { if (!open) setDialog(null); }} />}
      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete maintenance window?</AlertDialogTitle><AlertDialogDescription>Selected monitors will no longer be silenced by this window.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => void deleteWindow()} disabled={busy}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
